import {
  COACH_TEMPLATE_PREVIEW_NAMES,
  missingWelcomeTemplateParts,
  renderCoachTemplate,
  unknownCoachTemplatePlaceholders,
  type CoachTemplateView,
  type CoachWelcomeRequiredPart,
} from "@care-by-ella/contracts";

// TES-1355 — the Coachmallar editor's rules, kept out of the component so
// they can be tested.

export interface CoachTemplateEditorState {
  /** Placeholders the server will refuse, braces included. */
  unknown: string[];
  /** The text filled with the sample names, never a real patient's. */
  preview: string;
  /** The text differs from what is saved (the draft, else the live text). */
  dirty: boolean;
  /** Saving is allowed: non-empty, no unknown placeholder, and changed. */
  canSave: boolean;
  /**
   * TES-1402 (Ö-590) — parts the welcome template must keep that this text
   * lacks, in the admin's words. Saving is still allowed; publishing is not.
   */
  missing: string[];
  /**
   * Publishing is allowed: a saved draft exists, nothing is unsaved, and the
   * draft keeps the required parts.
   */
  canPublish: boolean;
}

export const WELCOME_REQUIRED_PART_TEXT: Record<
  CoachWelcomeRequiredPart,
  string
> = {
  coach_identity: "coachens namn ({coachens_förnamn})",
  non_medical_boundary:
    "att coachen inte besvarar medicinska frågor själv, utan tar dom vidare",
  medical_question_path:
    "vart medicinska frågor går (vårdteamet eller läkaren)",
};

export function savedText(view: CoachTemplateView): string {
  return view.draft?.body ?? view.body;
}

export function coachTemplateEditorState(
  view: CoachTemplateView,
  text: string,
): CoachTemplateEditorState {
  const unknown = unknownCoachTemplatePlaceholders(text);
  const dirty = text !== savedText(view);
  const missing =
    view.name === "welcome"
      ? missingWelcomeTemplateParts(text).map(
          (p) => WELCOME_REQUIRED_PART_TEXT[p],
        )
      : [];
  return {
    unknown,
    preview: renderCoachTemplate(text, COACH_TEMPLATE_PREVIEW_NAMES),
    dirty,
    canSave: dirty && text.trim().length > 0 && unknown.length === 0,
    missing,
    canPublish: view.draft !== null && !dirty && missing.length === 0,
  };
}

// The API's error codes, in the admin's words.
export function coachTemplateErrorText(code: string): string {
  switch (code) {
    case "coach_template_unknown_placeholder":
      return "Mallen innehåller en okänd platshållare. Tillåtna är {förnamn} och {coachens_förnamn}.";
    case "coach_template_missing_required_parts":
      return "Välkomstmallen kan inte publiceras: den måste innehålla coachens namn, att coachen inte besvarar medicinska frågor själv och vart medicinska frågor tas vidare.";
    case "coach_template_not_found":
      return "Mallen kunde inte hittas.";
    case "validation_failed":
      return "Mallen kunde inte sparas. Kontrollera texten.";
    default:
      return "Något gick fel. Försök igen.";
  }
}
